import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { getWebApiErrorMessage } from "@faire/web--source/common/getErrorMessage";
import { uploadImageFile } from "@faire/web--source/common/images/uploadImageFile";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { ImageUploader } from "@faire/web--source/slate/ImageUploader/ImageUploader";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Modal } from "@faire/web--source/slate/Modal";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useMutation as useMutationPatchUser } from "@faire/web-api--source/api/user/patch-hooks";
import { trackUpmarketMultiUserEducationBannerProfilePictureUpdateSubmit } from "@faire/web-api--source/events/upmarket/submit/multiUserEducationBannerProfilePictureUpdate";
import { trackUpmarketMultiUserEducationBannerProfilePictureUploadSubmit } from "@faire/web-api--source/events/upmarket/submit/multiUserEducationBannerProfilePictureUpload";
import * as React from "react";
import styled from "styled-components";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
var MAX_FILE_SIZE = 10 * 1024 * 1024;
export var ProfilePictureUploadModal = function(param) {
    var onClose = param.onClose, onProfilePictureUpdated = param.onProfilePictureUpdated;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var _React_useState = _sliced_to_array(React.useState(undefined), 2), error = _React_useState[0], setError = _React_useState[1];
    var _React_useState1 = _sliced_to_array(React.useState(undefined), 2), image = _React_useState1[0], setImage = _React_useState1[1];
    var _useUserStore = useUserStore([
        "userToken",
        "fetchUser"
    ]), userToken = _useUserStore.userToken, fetchUser = _useUserStore.fetchUser;
    var _useMutationPatchUser = useMutationPatchUser({
        onSuccess: function() {
            onProfilePictureUpdated === null || onProfilePictureUpdated === void 0 ? void 0 : onProfilePictureUpdated();
            fetchUser();
            onClose();
        },
        onError: function(error) {
            setError(getWebApiErrorMessage(error, strictLocalize));
        }
    }), updateUser = _useMutationPatchUser.mutate, isSavingProfilePicture = _useMutationPatchUser.isPending;
    var handleFileUpload = /*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(file) {
            var image, error, errorMessage;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        _state.trys.push([
                            0,
                            2,
                            ,
                            3
                        ]);
                        return [
                            4,
                            uploadImageFile(file)
                        ];
                    case 1:
                        image = _state.sent();
                        if (image.token && image.url) {
                            trackUpmarketMultiUserEducationBannerProfilePictureUploadSubmit();
                            setImage(image);
                        }
                        return [
                            3,
                            3
                        ];
                    case 2:
                        error = _state.sent();
                        errorMessage = getWebApiErrorMessage(error, strictLocalize);
                        setError(errorMessage);
                        return [
                            3,
                            3
                        ];
                    case 3:
                        return [
                            2
                        ];
                }
            });
        });
        return function handleFileUpload(file) {
            return _ref.apply(this, arguments);
        };
    }();
    var handleUploadError = function(message) {
        setError(message);
    };
    var handleProfilePictureSave = /*#__PURE__*/ function() {
        var _ref = _async_to_generator(function() {
            var updatedUser;
            return _ts_generator(this, function(_state) {
                trackUpmarketMultiUserEducationBannerProfilePictureUpdateSubmit();
                updatedUser = {
                    token: userToken,
                    profile_image: image
                };
                updateUser([
                    updatedUser
                ]);
                return [
                    2
                ];
            });
        });
        return function handleProfilePictureSave() {
            return _ref.apply(this, arguments);
        };
    }();
    var hasUploadedImage = !!image;
    return /*#__PURE__*/ _jsxs(Modal, {
        width: "383px",
        height: "auto",
        justify: "flex-start",
        align: "flex-start",
        padding: getSpacing("3x"),
        onClose: onClose,
        "data-test-id": "profile-picture-upload-modal",
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: "pageHeaderSerifRegular",
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "Hy/7gk",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Upload a photo"
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "4x"
            }),
            /*#__PURE__*/ _jsx(Typography, {
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "jAguup",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "We\u2019ll use this to represent your activity to your teammates and brands throughout the site."
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            }),
            /*#__PURE__*/ _jsxs(ContentWrapper, {
                children: [
                    /*#__PURE__*/ _jsx(ImageUploader, {
                        width: 200,
                        height: 200,
                        currentImage: image,
                        onFileUpload: handleFileUpload,
                        uploadCta: strictLocalize({
                            id: "GASpDw",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Upload JPEG, JPG, PNG, or WEBP"
                                }
                            ]
                        }),
                        maxFileSize: MAX_FILE_SIZE,
                        circular: hasUploadedImage,
                        error: !!error,
                        onUploadError: handleUploadError
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "1x"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "labelSansRegular",
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "8P4fvp",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Photo must be less than 10MB"
                                }
                            ]
                        })
                    })
                ]
            }),
            error ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "1x"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "labelSansRegular",
                        color: Core.icon.critical,
                        children: error
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsx(Spacer, {
                height: "4x"
            }),
            /*#__PURE__*/ _jsxs(ButtonWrapper, {
                children: [
                    /*#__PURE__*/ _jsx(StyledButton, {
                        disabled: !hasUploadedImage,
                        onClick: handleProfilePictureSave,
                        loading: isSavingProfilePicture,
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "ecIDoz",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Save"
                                }
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        width: "2x"
                    }),
                    /*#__PURE__*/ _jsx(StyledButton, {
                        variant: "secondary",
                        onClick: onClose,
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "tvzZWJ",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Cancel"
                                }
                            ]
                        })
                    })
                ]
            })
        ]
    });
};
var ContentWrapper = styled(Column).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-d051fb27-0"
})([
    "width:100%;"
]);
var ButtonWrapper = styled(Row).withConfig({
    componentId: "sc-d051fb27-1"
})([
    "width:100%;"
]);
var StyledButton = styled(Button).withConfig({
    componentId: "sc-d051fb27-2"
})([
    "width:100%;"
]);
